import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Consultation } from '@aldebaran/prisma';
import { startOfDay } from 'date-fns';
import * as Sentry from '@sentry/react';
import { recordResponses, updateConsultationDate } from '../reducers/graph';
import {
  consultationResponseIds,
  initialQuestionIds,
  responseIds,
} from '@aldebaran/common/constants/kb';
import { RootState } from '../types/graph';
import { isValidDate } from '@aldebaran/common/util/date';

export const useInjectConsultation = ({
  required = true,
}: { required?: boolean } = {}) => {
  const dispatch = useDispatch();

  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );

  const responses = useSelector((state: RootState) => state.kb.responses);
  // Carries the page the injection was originally requested from, so a retry
  // replays against that page rather than wherever the patient has navigated
  // to by the time the KB finishes loading (ALDO-2611).
  const pending = useRef<{
    date: Date;
    currentPage: typeof currentPage;
  } | null>(null);

  const injectGraphDate = useCallback(
    (dateObject: Date, page: typeof currentPage) => {
      dispatch(
        recordResponses({
          questionId: initialQuestionIds.CONSULTATION_DATE,
          currentPage: page,
          responses: [
            {
              ...responses[responseIds.CONSULTATION_DATE],
              value: { value: startOfDay(dateObject).getTime() },
            },
          ],
        }),
      );
      const time = new Date(0);
      time.setHours(dateObject.getHours());
      time.setMinutes(dateObject.getMinutes());
      dispatch(
        recordResponses({
          questionId: initialQuestionIds.CONSULTATION_TIME,
          currentPage: page,
          responses: [
            {
              ...responses[responseIds.CONSULTATION_TIME],
              value: { value: time.getTime() },
            },
          ],
        }),
      );
    },
    [dispatch, responses],
  );

  const injectConsultation = useCallback(
    ({ date }: Pick<Consultation, 'date'>) => {
      const missingResponseIds = consultationResponseIds.filter(
        (id) => !responses[id],
      );
      const hasResponses = missingResponseIds.length === 0;
      if (!hasResponses && required) {
        throw new Error('Missing consultation responses');
      }

      if (!isValidDate(date)) return;
      const dateObject = new Date(date);
      dispatch(updateConsultationDate(dateObject.toISOString()));
      if (!hasResponses) {
        pending.current = { date: dateObject, currentPage };
        Sentry.captureMessage(
          'Consultation graph injection skipped: KB responses missing',
          { level: 'warning', extra: { missingResponseIds } },
        );
        return;
      }
      pending.current = null;
      injectGraphDate(dateObject, currentPage);
    },
    [currentPage, dispatch, injectGraphDate, required, responses],
  );

  useEffect(() => {
    if (!pending.current) return;
    if (!consultationResponseIds.every((id) => responses[id])) return;
    const { date, currentPage: pendingPage } = pending.current;
    pending.current = null;
    // Best-effort retry; the Redux date dispatched above is the load-bearing
    // fix and survives even if this component unmounts before the KB loads.
    injectGraphDate(date, pendingPage);
  }, [injectGraphDate, responses]);

  return injectConsultation;
};
